/* RRR theme-scoped predictive search, desktop and mobile */
(() => {
  'use strict';
  class RRRSearchSuggestions {
    constructor(root) {
      if (root.dataset.rrrPredictiveInitialized) return;
      this.root = root;
      this.input = root.querySelector('[data-rrr-predictive-input]');
      this.panel = root.querySelector('[data-rrr-predictive-results]');
      this.form = this.input && this.input.closest('form');
      if (!this.input || !this.panel || !this.form) return;
      root.dataset.rrrPredictiveInitialized = 'true';
      this.currency = root.dataset.currency || window.Shopify?.currency?.active || 'INR';
      this.rootPath = root.dataset.root || window.Shopify?.routes?.root || '/';
      this.requestId = 0;
      this.controller = null;
      this.timer = null;
      this.selected = -1;
      this.dismissed = false;
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => {
        this.dismissed = false;
        if (this.input.value.trim().length >= 2) this.onInput();
      });
      this.input.addEventListener('keydown', event => this.onKeydown(event));
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
      });
      this.root.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.root.contains(document.activeElement)) this.close();
        }, 100);
      });
      document.addEventListener('pointerdown', event => {
        if (!this.root.contains(event.target)) this.close();
      });
    }

    close() {
      this.dismissed = true;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.selected = -1;
    }

    open() {
      if (this.dismissed || this.root.hidden) return;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    onInput() {
      window.clearTimeout(this.timer);
      this.requestId += 1;
      this.controller?.abort();
      this.selected = -1;
      this.dismissed = false;
      this.input.removeAttribute('aria-activedescendant');
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.panel.replaceChildren();
        this.close();
        return;
      }
      const id = this.requestId;
      this.timer = window.setTimeout(() => this.load(term, id), 180);
    }

    async load(term, id) {
      this.controller = new AbortController();
      this.showMessage('Searching products…');
      try {
        const rootUrl = new URL(this.rootPath, location.origin);
        const url = new URL('search/suggest.json', rootUrl);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,query');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url.toString(), {
          signal: this.controller.signal,
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Predictive search HTTP ' + response.status);
        const data = await response.json();
        if (id !== this.requestId || this.dismissed || term !== this.input.value.trim()) return;
        this.render(data?.resources?.results || {}, term);
      } catch (error) {
        if (error.name === 'AbortError' || id !== this.requestId) return;
        console.warn('RRR predictive search unavailable:', error);
        this.showMessage('Suggestions unavailable. Press Enter to search.');
      }
    }

    makeLink(urlValue, fallback) {
      try {
        const parsed = new URL(urlValue || fallback, location.origin);
        return parsed.origin === location.origin && /^https?:$/.test(parsed.protocol)
          ? parsed.href : fallback;
      } catch {
        return fallback;
      }
    }

    formatPrice(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency', currency: this.currency, maximumFractionDigits: 2
        }).format(amount);
      } catch {
        return '₹' + amount.toLocaleString('en-IN');
      }
    }

    addHeading(text) {
      const heading = document.createElement('div');
      heading.className = 'rrr-predictive__heading';
      heading.textContent = text;
      this.panel.append(heading);
    }

    addOption(label, href, image, detail, soldOut) {
      const item = document.createElement('a');
      item.href = this.makeLink(href, this.form.action);
      item.className = 'rrr-predictive__item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.dataset.rrrPredictiveOption = '';
      item.id = this.panel.id + '-option-' + this.panel.querySelectorAll('[data-rrr-predictive-option]').length;
      if (image) {
        const src = typeof image === 'string' ? image : (image.url || image.src);
        if (typeof src === 'string' && /^(https?:)?\/\//.test(src)) {
          const picture = document.createElement('img');
          picture.className = 'rrr-predictive__image';
          picture.src = src;
          picture.alt = '';
          picture.loading = 'lazy';
          item.append(picture);
        }
      }
      const textWrap = document.createElement('span');
      textWrap.className = 'rrr-predictive__copy';
      const name = document.createElement('span');
      name.className = 'rrr-predictive__name';
      name.textContent = label || 'Product';
      textWrap.append(name);
      if (detail || soldOut) {
        const subtitle = document.createElement('span');
        subtitle.className = 'rrr-predictive__detail';
        subtitle.textContent = soldOut ? ('Sold out' + (detail ? ' · ' + detail : '')) : detail;
        textWrap.append(subtitle);
      }
      item.append(textWrap);
      const arrow = document.createElement('span');
      arrow.className = 'rrr-predictive__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      item.append(arrow);
      this.panel.append(item);
    }

    addViewAll(term) {
      const searchUrl = new URL(this.form.action, location.origin);
      searchUrl.searchParams.set('q', term);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('options[prefix]', 'last');
      const link = document.createElement('a');
      link.className = 'rrr-predictive__view-all';
      link.href = searchUrl.toString();
      link.textContent = 'View all results for “' + term + '”  →';
      this.panel.append(link);
    }

    showMessage(message) {
      this.panel.replaceChildren();
      const line = document.createElement('p');
      line.className = 'rrr-predictive__message';
      line.textContent = message;
      this.panel.append(line);
      this.open();
    }

    render(results, term) {
      this.panel.replaceChildren();
      const products = Array.isArray(results.products) ? results.products.slice(0, 6) : [];
      const queries = Array.isArray(results.queries) ? results.queries.slice(0, 3) : [];
      if (products.length) {
        this.addHeading('Products');
        for (const product of products) {
          const image = product.featured_image || product.image;
          this.addOption(
            product.title, product.url, image,
            this.formatPrice(product.price), product.available === false
          );
        }
      }
      if (queries.length) {
        this.addHeading('Suggested searches');
        for (const suggestion of queries) {
          const text = suggestion.text || suggestion.query || term;
          this.addOption(text, suggestion.url, null, null, false);
        }
      }
      if (!products.length && !queries.length) {
        const info = document.createElement('p');
        info.className = 'rrr-predictive__message';
        info.textContent = 'No matching products found.';
        this.panel.append(info);
      }
      this.addViewAll(term);
      this.open();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.panel.hidden) {
          event.preventDefault();
          this.close();
        }
        return;
      }
      if (this.panel.hidden) return;
      const options = Array.from(this.panel.querySelectorAll('[data-rrr-predictive-option]'));
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && options.length) {
        event.preventDefault();
        this.selected = (this.selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, i) => {
          const isActive = i === this.selected;
          option.classList.toggle('is-active', isActive);
          option.setAttribute('aria-selected', String(isActive));
        });
        const option = options[this.selected];
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.selected >= 0 && options[this.selected]) {
        event.preventDefault();
        window.location.assign(options[this.selected].href);
      }
    }
  }

  function init(context = document) {
    if (context.matches?.('[data-rrr-predictive]')) new RRRSearchSuggestions(context);
    context.querySelectorAll?.('[data-rrr-predictive]').forEach(root => new RRRSearchSuggestions(root));
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
